import { useState, useEffect } from "react";
import { Row, Col, Card, Button, Form, Table } from "react-bootstrap";
import Breadcrumb from "app/components/Breadcrumb";
import api from "app/services/api";

const OUTWARD_TYPE_LABELS = {
  company_use: "Company Use",
  po_wise: "Part Wise",
  dispatch: "Dispatch",
};

const readOnlyStyle = { backgroundColor: "#f4f5f7", color: "#555", border: "1px solid #e0e0e0" };

const todayISO = () => new Date().toLocaleDateString("en-CA");

const lineAmount = (row) => {
  const qty = Number(row.qty);
  const unitPrice = Number(row.unit_price);
  if (!Number.isFinite(qty) || !Number.isFinite(unitPrice)) return 0;
  return qty * unitPrice;
};

export default function Outward() {
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [allPOs, setAllPOs] = useState([]);
  const [shops, setShops] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [productStockMap, setProductStockMap] = useState({});

  // Challan header
  const [challanNo, setChallanNo] = useState("");
  const [outwardDate, setOutwardDate] = useState(todayISO());
  const [poId, setPoId] = useState("");
  const [employee, setEmployee] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [shop, setShop] = useState("");

  // Product picker + the line items built up before saving
  const [category, setCategory] = useState("");
  const [product, setProduct] = useState("");
  const [items, setItems] = useState([]);

  const [successMsg, setSuccessMsg] = useState("");
  const [saving, setSaving] = useState(false);

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showAll, setShowAll] = useState(true);

  const showMessage = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  useEffect(() => {
    fetchOutwardEntries();

    api.get("/api/product-categories", { withCredentials: true })
      .then((res) => setCategories(res.data.productCategories || []))
      .catch(() => setCategories([]));

    api.get("/api/products", { withCredentials: true })
      .then((res) => setAllProducts(res.data.products || []))
      .catch(() => setAllProducts([]));

    api.get("/api/purchase-orders", { withCredentials: true })
      .then((res) => setAllPOs(res.data.purchase_orders || []))
      .catch(() => setAllPOs([]));

    api.get("/api/machinery-shops", { withCredentials: true })
      .then((res) => setShops(res.data.machineryShops || []))
      .catch(() => setShops([]));

    api.get("/api/employees/dropdown", { withCredentials: true })
      .then((res) => setEmployees(res.data.data || []))
      .catch(() => setEmployees([]));

    fetchStockMap();
  }, []);

  // Feeds the read-only Stock column on every product row.
  const fetchStockMap = () => {
    api.get("/api/stock/summary", { withCredentials: true })
      .then((res) => {
        const stockDetails = res.data.productDetails || [];
        const map = stockDetails.reduce((acc, item) => {
          acc[String(item.product_id)] = item.current_stock != null ? item.current_stock : 0;
          return acc;
        }, {});
        setProductStockMap(map);
      })
      .catch(() => setProductStockMap({}));
  };

  const fetchOutwardEntries = () => {
    setLoading(true);
    api
      .get("/api/outward", { withCredentials: true })
      .then((res) => setEntries(res.data.outwardEntries || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  };

  // Preview only - the real number is assigned under a lock when the challan saves.
  const fetchNextChallan = () => {
    api
      .get("/api/outward/next-challan", { withCredentials: true })
      .then((res) => setChallanNo(res.data.next_challan ?? ""))
      .catch(() => setChallanNo(""));
  };

  const handleDelete = (item) => {
    const confirmed = window.confirm(
      "Delete this outward entry? This will also change Stock and Stock Summary.",
    );
    if (!confirmed) return;

    api
      .delete("/api/outward/" + item.id, { withCredentials: true })
      .then(() => {
        fetchOutwardEntries();
        fetchStockMap();
        showMessage("Outward entry deleted successfully!");
      })
      .catch((err) => {
        const data = err.response?.data;
        showMessage(data?.message || "Error deleting outward entry!");
      });
  };

  // Products belonging to the picker's current category.
  const productsForCategory = category
    ? allProducts.filter((p) => String(p.category_id) === String(category))
    : allProducts;

  // Selecting a product appends it to the table below, mirroring the paper challan.
  const handleProductChange = (productId) => {
    setProduct("");
    if (!productId) return;

    const selected = allProducts.find((p) => String(p.id) === String(productId));
    if (!selected) return;

    if (items.some((item) => item.product_id === selected.id)) {
      showMessage(`${selected.product_name} is already added to this challan.`);
      return;
    }

    const stock = productStockMap[String(selected.id)];

    setItems((prev) => [
      ...prev,
      {
        product_id: selected.id,
        product_name: selected.product_name,
        category_id: selected.category_id,
        unit: selected.unit || "",
        stock: stock != null ? Number(stock) : 0,
        unit_price: selected.unit_price ?? "",
        qty: "",
      },
    ]);
  };

  const handleItemChange = (index, field, value) =>
    setItems((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );

  const removeItem = (index) =>
    setItems((prev) => prev.filter((_, i) => i !== index));

  const resetForm = () => {
    setOutwardDate(todayISO());
    setPoId("");
    setEmployee("");
    setEmployeeName("");
    setShop("");
    setCategory("");
    setProduct("");
    setItems([]);
  };

  const openForm = () => {
    resetForm();
    fetchNextChallan();
    fetchStockMap();
    setShowForm(true);
  };

  const grandTotal = items.reduce((sum, row) => sum + lineAmount(row), 0);

  const handleSave = () => {
    if (!outwardDate) { showMessage("Please select outward date!"); return; }
    if (!shop) { showMessage("Please select a shop!"); return; }
    if ((employees.length === 0 || employee === "__new__") && !employeeName.trim()) {
      showMessage("Please enter the employee name!");
      return;
    }
    if (items.length === 0) { showMessage("Please add at least one product!"); return; }

    for (let i = 0; i < items.length; i += 1) {
      const row = items[i];
      if (!row.qty || Number(row.qty) <= 0) {
        showMessage(`Please enter a valid quantity for ${row.product_name}!`);
        return;
      }
      if (Number(row.qty) > Number(row.stock)) {
        showMessage(`Cannot outward ${row.qty} of ${row.product_name}. Available stock is ${row.stock}.`);
        return;
      }
      if (row.unit_price !== "" && Number(row.unit_price) < 0) {
        showMessage(`Please enter a valid price for ${row.product_name}!`);
        return;
      }
    }

    const selectedPO = allPOs.find((po) => String(po.id) === String(poId));

    setSaving(true);
    api
      .post(
        "/api/outward",
        {
          outward_date: outwardDate,
          po_id: poId || null,
          po_number: selectedPO?.po_number || null,
          employee_id: employee && employee !== "__new__" ? employee : null,
          received_by: employees.length === 0 || employee === "__new__" ? employeeName.trim() : null,
          shop_id: shop,
          items: items.map((row) => ({
            product_id: row.product_id,
            category_id: row.category_id,
            qty: row.qty,
            unit_price: row.unit_price,
          })),
        },
        { withCredentials: true },
      )
      .then((res) => {
        showMessage(res.data?.message || "Outward saved successfully!");
        resetForm();
        setShowForm(false);
        setCurrentPage(1);
        fetchOutwardEntries();
        fetchStockMap();
      })
      .catch((err) => {
        const data = err.response?.data;
        showMessage(data?.message || data?.error || "Error saving outward entry!");
      })
      .finally(() => setSaving(false));
  };

  const filteredEntries = entries.filter((item) => {
    const term = search.toLowerCase();
    return (
      (item.product_name || "").toLowerCase().includes(term) ||
      (item.category_name || "").toLowerCase().includes(term) ||
      (item.shop_name || "").toLowerCase().includes(term) ||
      String(item.challan_no || "").toLowerCase().includes(term)
    );
  });

  const totalRows = filteredEntries.length;
  const totalPages = rowsPerPage >= totalRows ? 1 : Math.ceil(totalRows / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedEntries = showAll
    ? filteredEntries
    : filteredEntries.slice(startIndex, startIndex + rowsPerPage);

  return (
    <section>
      <Breadcrumb routeSegments={[
        { name: "Dashboard", path: "/" },
        { name: "Product Management", path: "/product" },
        { name: "Outward" },
      ]} />

      {successMsg && (
        <div className="alert alert-success d-flex align-items-center gap-2 mb-3" role="alert">
          <i className="bi bi-check-circle-fill"></i>
          {successMsg}
        </div>
      )}

      {!showForm && (
        <>
          <Card body className="mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <Button
                style={{ backgroundColor: "#663399", border: "none" }}
                onClick={openForm}
              >
                Outward Items
              </Button>

              <input
                type="text"
                placeholder="Search Challan / Product / Category / Shop"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  width: "300px",
                  height: "38px",
                  paddingLeft: "10px",
                  border: "1px solid #d1d1d1",
                }}
              />
            </div>
          </Card>

          <Card body>
            <Card.Title className="mb-4">Product Management - Outward</Card.Title>

            <div style={{ overflowX: "auto" }}>
              <Table bordered hover className="align-middle">
                <thead style={{ backgroundColor: "#cfc7d9" }}>
                  <tr>
                    <th>No</th>
                    <th>Challan No</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Product Name</th>
                    <th>Category Name</th>
                    <th>Shop</th>
                    <th>Employee</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan="11" className="text-center">Loading...</td>
                    </tr>
                  )}

                  {!loading && paginatedEntries.length === 0 && (
                    <tr>
                      <td colSpan="11" className="text-center">No outward entries found.</td>
                    </tr>
                  )}

                  {!loading &&
                    paginatedEntries.map((item, index) => (
                      <tr key={item.id}>
                        <td>{startIndex + index + 1}</td>
                        <td>{item.challan_no || "—"}</td>
                        <td>
                          {item.outward_date
                            ? new Date(item.outward_date).toLocaleDateString("en-CA")
                            : (item.created_at
                              ? new Date(item.created_at).toLocaleDateString("en-CA")
                              : "-")}
                        </td>
                        <td>{OUTWARD_TYPE_LABELS[item.outward_type] || item.outward_type || "-"}</td>
                        <td>{item.product_name || "-"}</td>
                        <td>{item.category_name || "-"}</td>
                        <td>{item.shop_name || "—"}</td>
                        <td>{item.employee_name || item.received_by || "—"}</td>
                        <td>{item.qty}</td>
                        <td>{item.price != null ? Number(item.price).toFixed(2) : "—"}</td>
                        <td>
                          <Button
                            size="sm"
                            style={{ backgroundColor: "#c4302b", border: "none" }}
                            onClick={() => handleDelete(item)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </div>

            <div className="d-flex justify-content-end align-items-center mt-3">
              <span className="me-2">Rows per page</span>
              <Form.Select
                value={showAll ? "all" : rowsPerPage}
                onChange={(e) => {
                  if (e.target.value === "all") {
                    setShowAll(true);
                    setRowsPerPage(filteredEntries.length || 5);
                  } else {
                    setShowAll(false);
                    setRowsPerPage(Number(e.target.value));
                  }
                  setCurrentPage(1);
                }}
                style={{ width: "90px", marginRight: "15px" }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value="all">All</option>
              </Form.Select>

              <Button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Prev
              </Button>
              <span className="mx-3">
                {currentPage} / {totalPages || 1}
              </span>
              <Button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </Button>
            </div>
          </Card>
        </>
      )}

      {showForm && (
        <Card body>
          <Card.Title className="mb-4">Add Outward Item</Card.Title>

          <Form>
            <Row className="g-3">
              <Col md={3}>
                <Form.Label className="fw-semibold" style={{ fontSize: "0.88rem" }}>
                  Challan Number
                </Form.Label>
                <Form.Control value={challanNo || "…"} readOnly style={readOnlyStyle} />
              </Col>

              <Col md={3}>
                <Form.Label className="fw-semibold" style={{ fontSize: "0.88rem" }}>
                  Select Part (Reg No.)
                </Form.Label>
                <Form.Select value={poId} onChange={(e) => setPoId(e.target.value)}>
                  <option value="">Search or select Part</option>
                  {allPOs.map((po) => (
                    <option key={po.id} value={po.id}>
                      {po.po_number}{po.vendor_name ? ` — ${po.vendor_name}` : ""}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={3}>
                <Form.Label className="fw-semibold" style={{ fontSize: "0.88rem" }}>
                  Select Employee
                </Form.Label>
                {employees.length === 0 ? (
                  <Form.Control
                    type="text"
                    value={employeeName}
                    onChange={(e) => setEmployeeName(e.target.value)}
                    placeholder="Enter employee name"
                    aria-label="Employee name"
                  />
                ) : (
                  <>
                    <Form.Select
                      value={employee}
                      onChange={(e) => {
                        setEmployee(e.target.value);
                        setEmployeeName("");
                      }}
                    >
                      <option value="">Select Employee</option>
                      {employees.map((emp) => (
                        <option key={emp.id} value={emp.id}>{emp.employee}</option>
                      ))}
                      <option value="__new__">Add employee name...</option>
                    </Form.Select>
                    {employee === "__new__" && (
                      <Form.Control
                        className="mt-2"
                        type="text"
                        value={employeeName}
                        onChange={(e) => setEmployeeName(e.target.value)}
                        placeholder="Enter employee name"
                        aria-label="New employee name"
                      />
                    )}
                  </>
                )}
              </Col>

              <Col md={3}>
                <Form.Label className="fw-semibold" style={{ fontSize: "0.88rem" }}>
                  Select Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={outwardDate}
                  onChange={(e) => setOutwardDate(e.target.value)}
                />
              </Col>

              <Col md={4}>
                <Form.Label className="fw-semibold" style={{ fontSize: "0.88rem" }}>
                  Select Shop <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select value={shop} onChange={(e) => setShop(e.target.value)}>
                  <option value="">Select Shop</option>
                  {shops.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.shop_name}{s.machine_no ? ` (${s.machine_no})` : ""}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={4}>
                <Form.Label className="fw-semibold" style={{ fontSize: "0.88rem" }}>
                  Select Category
                </Form.Label>
                <Form.Select
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    setProduct("");
                  }}
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={4}>
                <Form.Label className="fw-semibold" style={{ fontSize: "0.88rem" }}>
                  Select Product
                </Form.Label>
                <Form.Select
                  value={product}
                  onChange={(e) => handleProductChange(e.target.value)}
                >
                  <option value="">Select Product</option>
                  {productsForCategory.map((p) => (
                    <option key={p.id} value={p.id}>{p.product_name}</option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={12}>
                <hr style={{ borderColor: "#eee", margin: "4px 0" }} />
              </Col>

              <Col md={12}>
                <div style={{ overflowX: "auto" }}>
                  <Table bordered className="align-middle">
                    <thead style={{ backgroundColor: "#cfc7d9" }}>
                      <tr>
                        <th style={{ minWidth: "200px" }}>Product Name</th>
                        <th style={{ minWidth: "90px" }}>Unit</th>
                        <th style={{ minWidth: "90px" }}>Stock</th>
                        <th style={{ minWidth: "120px" }}>Price</th>
                        <th style={{ minWidth: "120px" }}>Qty</th>
                        <th style={{ minWidth: "120px" }}>Amount</th>
                        <th style={{ width: "60px" }} />
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 && (
                        <tr>
                          <td colSpan="7" className="text-center text-muted">
                            Select a category and product above to add rows.
                          </td>
                        </tr>
                      )}

                      {items.map((row, index) => {
                        const overStock =
                          row.qty !== "" && Number(row.qty) > Number(row.stock);

                        return (
                          <tr key={row.product_id}>
                            <td>
                              <Form.Control value={row.product_name} readOnly style={readOnlyStyle} />
                            </td>
                            <td>
                              <Form.Control value={row.unit} readOnly style={readOnlyStyle} />
                            </td>
                            <td>
                              <Form.Control value={row.stock} readOnly style={readOnlyStyle} />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                min="0"
                                step="0.01"
                                value={row.unit_price}
                                onChange={(e) => handleItemChange(index, "unit_price", e.target.value)}
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                min="0"
                                value={row.qty}
                                onChange={(e) => handleItemChange(index, "qty", e.target.value)}
                                style={overStock ? { borderColor: "#dc3545" } : {}}
                              />
                              {overStock && (
                                <Form.Text className="text-danger" style={{ fontSize: "0.8rem" }}>
                                  ⚠ Cannot outward {row.qty}. Available stock is {row.stock}.
                                </Form.Text>
                              )}
                            </td>
                            <td>
                              <Form.Control
                                value={lineAmount(row).toFixed(2)}
                                readOnly
                                style={readOnlyStyle}
                              />
                            </td>
                            <td className="text-center">
                              <Button
                                size="sm"
                                style={{ backgroundColor: "#c4302b", border: "none" }}
                                onClick={() => removeItem(index)}
                                title="Remove row"
                              >
                                <i className="i-Close" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}

                      {items.length > 0 && (
                        <tr>
                          <td colSpan="5" className="text-end fw-semibold">Total</td>
                          <td className="fw-semibold">{grandTotal.toFixed(2)}</td>
                          <td />
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Col>

              <Col md={12} className="mt-2 d-flex gap-2">
                <Button
                  variant="secondary"
                  style={{ padding: "10px 44px", borderRadius: "6px", fontWeight: 600 }}
                  onClick={() => {
                    resetForm();
                    setShowForm(false);
                  }}
                >
                  Back
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    backgroundColor: "#663399",
                    border: "none",
                    padding: "10px 44px",
                    borderRadius: "6px",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    letterSpacing: "0.3px",
                    boxShadow: "0 2px 8px rgba(102,51,153,0.25)",
                  }}
                >
                  {saving ? "Saving..." : "Submit"}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card>
      )}
    </section>
  );
}
